// Ternion's page: wires the screens together (title, the lattice, the
// table), the Codex, the Glimmer (and the augury,
// the Glimmer pressed again) and the Rosetta, and the Monad at the end of
// each rite. Every effect (storage, time, sound,
// motion, the solver's worker) comes in as an argument, so a test can hand
// in its own.

import { ICONS, ICON_IDS, type IconId } from "../config/icons.ts"
import { RULES } from "../config/rules.ts"
import { MOTION } from "../config/motion.ts"
import { TEASER } from "../config/teaser.ts"
import { GAME_VERSION } from "../config/version.ts"
import { WORDS } from "../config/words.ts"
import type { LawId } from "../config/laws.ts"
import { renderHtml } from "../boundary/html.ts"
import { decodeSave, encodeSave, type Saved } from "../boundary/save.ts"
import { reversed } from "../domain/arrays.ts"
import { legalMoves, type CardId, type Move, type TableState } from "../domain/engine.ts"
import { findFragments } from "../domain/fragments.ts"
import { canUndo, gameWon, newGame, play, restart, resume, savedOf, spend, undo, withRosetta, type Game } from "../domain/game.ts"
import { EMPTY_PROGRESS, noteSeen, setPlaying, setRosetta, witness, type Progress } from "../domain/progress.ts"
import { ALL_RITE_IDS, TERNION_ID, ternionOf, type LatticeRite, type Rite, type WonKey } from "../domain/rite.ts"
import { ternionState } from "../domain/lattice.ts"
import { digits, ringsOf, type GlyphKey } from "../domain/rings.ts"
import { recordWin } from "../domain/wins.ts"
import { wayFrom, waysAlong } from "../domain/way.ts"
import { keysOn, sightingsOf } from "../domain/witness.ts"
import type { Cancel, Clock } from "../effects/clock.ts"
import type { Motion } from "../effects/motion.ts"
import type { Sound } from "../effects/sound.ts"
import type { Storage } from "../effects/storage.ts"
import type { Thinker } from "../effects/thinker.ts"
import { createCodex } from "./codex.ts"
import { closest, isHidden, part, parts, replay } from "./dom.ts"
import { realmOf, sigil } from "./glyph.ts"
import { createLattice, numeral } from "./lattice.ts"
import { startSky } from "./sky.ts"
import { createTable } from "./table.ts"
import { celebrate } from "./veil.ts"

export type AppDeps = {
  readonly doc: Document
  readonly rites: readonly LatticeRite[]
  readonly storage: Storage
  readonly clock: Clock
  readonly motion: Motion
  readonly sound: Sound
  readonly thinker: Thinker
  readonly hash: string
}

// What the integration tests (tests/integration/) may read and do.
export type AppHandle = {
  readonly saved: () => Saved
  readonly game: () => Game | undefined
  readonly screen: () => Screen
  readonly attempt: (move: Move, ids: readonly CardId[]) => Promise<boolean>
  readonly startRite: (id: WonKey) => void
  readonly idle: () => Promise<void> // resolves when no move is being played out
}

type Screen = "title" | "map" | "play"

const iconSvg = (id: IconId): string => `<svg viewBox="0 0 24 24" aria-hidden="true">${ICONS[id].svg}</svg>`
const asIcon = (s: string | undefined): IconId | undefined => ICON_IDS.find((i) => i === s)

export const startApp = (deps: AppDeps): AppHandle => {
  const { doc, clock, sound, motion, storage, thinker } = deps
  const $ = (selector: string): HTMLElement => part(doc, selector)
  const exists = (id: WonKey): boolean => deps.rites.some((r) => r.id === id)
  // The last rite is made from the first (src/domain/rite.ts).
  const firstRite = deps.rites.find((r) => r.id === RULES.firstRite)
  const ternion = firstRite === undefined ? undefined : ternionOf(firstRite)

  let saved: Saved = decodeSave(storage.read())
  let game: Game | undefined
  let screen: Screen = "title"
  let busy: Promise<void> | undefined
  let justWon: WonKey | "none" = "none"
  let deadTicket = 0
  let idleCancel: Cancel = () => undefined
  let ways: ReadonlyMap<string, readonly Move[]> = new Map() // a way the augury found, by table
  let seeking = false
  let glimmeredOn: TableState | undefined // the table the Glimmer last helped on

  const progress = (): Progress => saved.progress
  const save = (): void => storage.write(encodeSave(saved))
  const change = (f: (p: Progress) => Progress): void => {
    saved = { ...saved, progress: f(saved.progress) }
    save()
  }

  // ---------- glyphs on the buttons ----------

  for (const el of parts(doc, "[data-icon]")) {
    const id = asIcon(el.dataset["icon"])
    if (id !== undefined) el.innerHTML = iconSvg(id)
  }

  // The version in tiny print in a corner, so a player can say which they saw.
  $("#version").textContent = `v${GAME_VERSION}`

  const skyCanvas = doc.querySelector("#sky")
  const sky = startSky(skyCanvas instanceof HTMLCanvasElement ? skyCanvas : doc.createElement("canvas"), motion.reduced)
  const fab = $("#glimmer")
  const tableRoot = $("#table")

  // ---------- views ----------

  const table = createTable(tableRoot, { sound, motion, clock, attempt: (m, ids) => attempt(m, ids) })
  // A change made in the Codex can find a fragment too: the last glyph set in the Order.
  const codex = createCodex($("#codex"), {
    sound,
    clock,
    progress,
    change: (next) => {
      change(() => next)
      noteFragments()
    },
  })
  const lattice = createLattice($("#lattice"), $("#map-progress"), {
    sound,
    clock,
    exists,
    par: (id) => deps.rites.find((r) => r.id === id)?.par,
    open: (id) => startRite(id),
    ternion: () => startRite(TERNION_ID),
  })

  // ---------- screens ----------

  function showScreen(name: Screen): void {
    const inPlace = screen === name
    screen = name
    storage.writeScreen(name === "play" && game !== undefined ? `play:${game.rite.id}` : name)
    for (const s of ["title", "map", "play"] as const) $(`#${s}`).hidden = s !== name
    doc.body.dataset["screen"] = name
    if (name === "map") {
      lattice.render(progress(), justWon, inPlace)
      justWon = "none"
    }
  }

  // ---------- the bar ----------

  const updateBar = (): void => {
    const g = game
    if (g === undefined) return
    const mark = $("#rite-mark")
    const r = g.rite
    mark.innerHTML = renderHtml(sigil(ringsOf(r.id)))
    mark.dataset["layer"] = r.kind === "lattice" ? r.id.slice(2) : "whole"
    $("#rite-count").innerHTML = renderHtml(numeral(g.made, RULES.counterDigits))
    for (const act of ["undo", "restart"]) {
      const b = $(`#play [data-act="${act}"]`)
      if (b instanceof HTMLButtonElement) b.disabled = !canUndo(g)
    }
    const ros = $('#play [data-act="rosetta"]')
    ros.classList.toggle("on", progress().rosetta)
    ros.setAttribute("aria-pressed", String(progress().rosetta))
  }

  const keep = (g: Game): void => {
    game = g
    change((p) => setPlaying(p, g.rite.id, savedOf(g)))
  }

  // ---------- laws witnessed ----------

  const see = (law: LawId, example: readonly GlyphKey[]): void => {
    const w = witness(progress(), law, example)
    change(() => w.progress)
    if (w.first) codex.news()
  }

  const noteFragments = (): void => {
    const f = findFragments(progress(), clock.now())
    if (f.found.length === 0) return
    change(() => f.progress)
    codex.news()
  }

  // ---------- the first rite's quiet help ----------

  // On the very first rite, a player who has not moved for a while is shown
  // one move as a ghost. It is free: it costs no gold.
  const nudgeLater = (): void => {
    idleCancel()
    if (game?.rite.id === RULES.firstRite && progress().solved[RULES.firstRite] === undefined) idleCancel = clock.after(RULES.idleGhostMs, nudge)
  }

  const panelsOpen = (): boolean => codex.isOpen()

  function nudge(): void {
    const g = game
    if (g === undefined || g.rite.id !== RULES.firstRite || gameWon(g)) return
    const s = g.state
    const move = legalMoves(s, { prune: false }).find((m) => m.to.kind === "col" && (s.cols[m.to.i]?.cards.length ?? 0) > 0)
    if (busy === undefined && screen === "play" && !panelsOpen() && move !== undefined) void table.ghost(move)
    nudgeLater()
  }

  // After a move, the solver checks quietly whether any path is left. If
  // none, the glimmer darkens. It never says what to do unless asked.
  const watchForDeadEnd = (): void => {
    const g = game
    if (g === undefined) return
    const ticket = ++deadTicket
    fab.classList.remove("no-path")
    if (legalMoves(g.state, { prune: false }).length === 0) {
      fab.classList.add("no-path")
      tableRoot.classList.add("asleep")
      return
    }
    tableRoot.classList.remove("asleep")
    void thinker.pathExists(g.state).then((v) => {
      if (ticket === deadTicket && v === "none") fab.classList.add("no-path")
    })
  }

  // ---------- playing ----------

  async function attempt(move: Move, ids: readonly CardId[]): Promise<boolean> {
    const g = game
    if (busy !== undefined || g === undefined) return false
    nudgeLater()
    const r = play(g, move)
    if (r.tag === "error") {
      table.refuse(move, r.error, ids)
      if (r.error.why === "burden") see("burden", keysOn(g.state, ids))
      return false
    }
    let finish: () => void = () => undefined
    busy = new Promise((resolve) => {
      finish = resolve
    })
    try {
      const events = r.value.events
      game = r.value.game
      for (const s of sightingsOf(events)) see(s.law, s.example)
      if (events.some((e) => e.type === "fold")) sky.pulse(0.6)
      await table.play(events)
      change((p) => noteSeen(p, r.value.game.state))
      keep(r.value.game)
      updateBar()
      if (gameWon(r.value.game)) await win(r.value.game)
      else watchForDeadEnd()
    } finally {
      busy = undefined
      finish()
    }
    return true
  }

  async function win(g: Game): Promise<void> {
    const rite = g.rite
    const first = progress().solved[rite.id] === undefined
    const pure = g.aid === 0
    change((p) => recordWin(p, rite, { made: g.made, aid: g.aid }, clock.now()))
    noteFragments()
    sound.monad(g.state.width)
    sky.pulse(1)
    // The made Monad fades from the table as the veil's Monad rises, so it
    // never shows through the veil behind it.
    tableRoot.classList.add("won")
    await celebrate($("#veil"), clock, { moves: g.made, par: rite.par, pure })
    tableRoot.classList.remove("won")
    game = undefined
    justWon = first ? rite.id : "none"
    showScreen("map")
  }

  function begin(rite: Rite): void {
    tableRoot.classList.remove("won")
    const saved = progress().playing[rite.id]
    const dealt = saved === undefined ? newGame(rite) : resume(rite, saved)
    const g = progress().rosetta ? withRosetta(dealt) : dealt
    game = g
    ways = new Map()
    showScreen("play")
    tableRoot.classList.remove("asleep")
    fab.classList.remove("no-path")
    tableRoot.dataset["width"] = String(rite.deal.width)
    table.clearSelection()
    table.show(g.start, g.state, "instant")
    table.setRosetta(progress().rosetta)
    change((p) => noteSeen(p, g.state))
    updateBar()
    watchForDeadEnd()
    nudgeLater()
  }

  function startRite(id: WonKey): void {
    if (id === TERNION_ID) {
      if (ternion !== undefined && ternionState(progress()) !== "locked") begin(ternion)
      return
    }
    const rite = deps.rites.find((r) => r.id === id)
    if (rite !== undefined) begin(rite)
  }

  const resumeKey = (key: string): void => {
    const lat = key === TERNION_ID ? TERNION_ID : ALL_RITE_IDS.find((id) => id === key)
    if (lat === TERNION_ID || (lat !== undefined && exists(lat))) startRite(lat)
    else showScreen("map")
  }

  // Take back n moves at once, as the undo button does one at a time.
  const rewind = (g: Game, n: number): Game => {
    table.clearSelection()
    const back = Array.from({ length: n }).reduce<Game>((x) => undo(x), g)
    table.show(back.start, back.state, "glide")
    keep(back)
    updateBar()
    watchForDeadEnd()
    return back
  }

  const takeBack = (): void => {
    const g = game
    if (busy !== undefined || g === undefined || !canUndo(g)) return
    nudgeLater()
    rewind(g, 1)
  }

  const again = (): void => {
    const g = game
    if (busy !== undefined || g === undefined || !canUndo(g)) return
    nudgeLater()
    table.clearSelection()
    const fresh = restart(g)
    table.show(fresh.start, fresh.state, "glide")
    keep(fresh)
    updateBar()
    watchForDeadEnd()
  }

  // ---------- help: the glimmer and the Rosetta ----------

  const glimmer = (): void => {
    const g = game
    if (g === undefined || busy !== undefined) return
    sound.wake()
    const all = legalMoves(g.state, { prune: false })
    const onCards = all.filter((m) => m.to.kind === "col" && (g.state.cols[m.to.i]?.cards.length ?? 0) > 0)
    const pick = onCards.length > 0 ? onCards : all
    if (pick.length === 0) {
      sound.refuse()
      replay(fab, "nope")
      return
    }
    const ids = [...new Set(pick.flatMap((m) => table.liftedIds(m).slice(0, 1)))]
    keep(spend(g, RULES.aid.glimmer))
    sound.chime(2)
    replay(fab, "burst")
    table.glimmer(ids)
    updateBar()
  }

  // The augury, the Glimmer pressed again on the same table: the solver
  // looks for a way to the Monad from it, and a ghost shows its next move. From a dead end
  // it looks back, takes the moves back to the last table that had a way,
  // and shows the move from there. Nothing found, nothing paid: the spark
  // shakes, and if no table behind had a way either, "begin again" stirs.
  const showWay = async (g: Game, moves: readonly Move[]): Promise<void> => {
    const next = moves[0]
    if (next === undefined) return
    keep(spend(g, RULES.aid.augury))
    glimmeredOn = g.state // pressed again here, it shows the way again
    updateBar()
    sound.chime(5)
    replay(fab, "burst")
    await table.ghost(next)
  }

  // The rite as it is now, if its table has not changed since `g`.
  const stillAt = (g: Game): Game | undefined => (game !== undefined && game.state === g.state ? game : undefined)

  const noWay = (proven: boolean): void => {
    sound.refuse()
    replay(fab, "nope")
    if (proven) replay($('#play [data-act="restart"]'), "news")
  }

  const lookBack = async (g: Game): Promise<void> => {
    const before = reversed(g.history.slice(-RULES.solver.wayBackMoves).map((h) => h.state))
    const behind = await thinker.findWay(before, RULES.solver.wayBackNodes)
    const now = stillAt(g)
    if (now === undefined) return
    if (behind.tag !== "way") return noWay(behind.tag === "none")
    const back = rewind(now, behind.at + 1)
    ways = waysAlong(back.state, behind.moves)
    await showWay(back, behind.moves)
  }

  const seekWay = async (): Promise<void> => {
    const g = game
    if (g === undefined || busy !== undefined || seeking || gameWon(g)) return
    sound.wake()
    const known = wayFrom(ways, g.state)
    if (known !== undefined) return showWay(g, known)
    seeking = true
    fab.classList.add("seeking")
    try {
      const ahead = await thinker.findWay([g.state], RULES.solver.wayNodes)
      const now = stillAt(g)
      if (now === undefined) return
      if (ahead.tag === "unknown") return noWay(false)
      if (ahead.tag === "none") return await lookBack(now)
      ways = waysAlong(now.state, ahead.moves)
      await showWay(now, ahead.moves)
    } finally {
      seeking = false
      fab.classList.remove("seeking")
    }
  }

  // The Glimmer button. A press lights the cards that can move; pressed
  // again with the table as it was (no move made or taken back since), it
  // shows the next move on a way to the Monad. Once the table changes, the
  // next press is a glimmer again. No double press to find.
  const pressGlimmer = (): void => {
    const g = game
    if (g === undefined || busy !== undefined) return
    if (glimmeredOn === g.state) return void seekWay()
    glimmeredOn = g.state
    glimmer()
  }

  const toggleRosetta = (): void => {
    const on = !progress().rosetta
    const g = game
    if (on && g !== undefined) keep(withRosetta(g))
    change((p) => setRosetta(p, on))
    sound.chime(on ? 3 : -3)
    table.setRosetta(on)
    updateBar()
  }

  // ---------- buttons and keys ----------

  const toggleSound = (): void => {
    saved = { ...saved, muted: !saved.muted }
    sound.setMuted(saved.muted)
    doc.body.classList.toggle("muted", saved.muted)
    save()
  }

  // Begin anew: the spiral under the tablets (drawn by the lattice). The
  // first press arms it: it asks, with a question mark, and knocks. A second
  // press soon after forgets every rite won, every note and every glyph
  // seen; the sound setting stays. No words on it: they show only on hover
  // and to screen readers.
  let armedAt: number | undefined
  const armed = (): boolean => armedAt !== undefined && clock.now() - armedAt < MOTION.ms.confirm
  const drawAnew = (): void => {
    const b = doc.querySelector('#lattice [data-act="anew"]')
    if (!(b instanceof HTMLElement)) return
    b.classList.toggle("armed", armed())
    const label = armed() ? WORDS.anew.confirm : WORDS.anew.ask
    b.setAttribute("aria-label", label)
    b.setAttribute("title", label)
  }
  const anew = (): void => {
    if (armed()) {
      armedAt = undefined
      game = undefined
      change(() => EMPTY_PROGRESS)
      sound.chime(-6)
      return showScreen("map")
    }
    armedAt = clock.now()
    sound.refuse()
    drawAnew()
    clock.after(MOTION.ms.confirm + 100, drawAnew)
  }

  const act = (name: string): void => {
    sound.wake()
    switch (name) {
      case "lattice":
        return showScreen("map")
      case "title":
        return showScreen("title")
      case "undo":
        return takeBack()
      case "restart":
        return again()
      case "rosetta":
        return toggleRosetta()
      case "book":
        return codex.toggle()
      case "glimmer":
        return pressGlimmer()
      case "anew":
        return anew()
      case "sound":
        return toggleSound()
    }
  }

  doc.addEventListener("click", (e) => {
    const b = closest(e, "[data-act]")
    const name = b?.dataset["act"]
    if (name !== undefined) act(name)
  })

  // Keys go to the table when it is showing and no panel is open; Enter on
  // a focused button still presses the button.
  const tableKeys = (e: KeyboardEvent): boolean => {
    if (screen !== "play" || panelsOpen() || !isHidden($("#veil"))) return false
    return !(closest(e, "button") !== undefined && (e.key === "Enter" || e.key === " "))
  }

  doc.addEventListener("keydown", (e) => {
    if (closest(e, "textarea, input") !== undefined) return
    const k = e.key.toLowerCase()
    if ((e.ctrlKey || e.metaKey) && k === "z") {
      e.preventDefault()
      return takeBack()
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (tableKeys(e) && table.key(e.key)) return e.preventDefault()
    if (k === "z" || k === "backspace") return takeBack()
    if (k === "g" && screen === "play") return pressGlimmer()
    if (k === "r" && screen === "play") return toggleRosetta()
    if (k === "c") return act("book")
    if (k === "escape") {
      codex.close()
      table.clearSelection()
    }
  })

  // ---------- the title ----------

  $("#to-teaser").setAttribute("href", TEASER.path)
  $("#to-teaser").setAttribute("aria-label", WORDS.links.watch)

  // The title sigil drifts through the cube, one glyph at a time.
  let drift = 0
  const titleSigil = $("#title-sigil")
  const step = (): void => {
    if (screen !== "title") return
    const t = digits(drift, 3)
    titleSigil.innerHTML = renderHtml(sigil(t))
    titleSigil.dataset["realm"] = realmOf(t)
    drift = drift >= 13 ? -13 : drift + 1
  }
  step()
  clock.every(MOTION.ms.titleDrift, step)

  $("#begin").addEventListener("click", () => {
    sound.wake()
    sound.chime(0)
    const p = progress()
    const firstTime = p.solved[RULES.firstRite] === undefined && p.playing[RULES.firstRite] === undefined
    if (firstTime) startRite(RULES.firstRite)
    else showScreen("map")
  })

  // ---------- start ----------

  sound.setMuted(saved.muted)
  doc.body.classList.toggle("muted", saved.muted)
  const hash = deps.hash.replace("#", "")
  const back = storage.readScreen()
  if (hash === "map") showScreen("map")
  else if (hash.startsWith("rite-")) startRite(ALL_RITE_IDS.find((id) => id === hash.slice(5).replace(/p/g, "+").replace(/m/g, "-")) ?? RULES.firstRite)
  else if (back === "map") showScreen("map")
  else if (back?.startsWith("play:") === true) resumeKey(back.slice(5))
  // The lattice is the way in: a new player finds one rite open, in the
  // middle. The title is one press away (the bar's first button).
  else showScreen("map")

  return {
    saved: () => saved,
    game: () => game,
    screen: () => screen,
    attempt,
    startRite,
    idle: () => busy ?? Promise.resolve(),
  }
}
